import React, { useState, useEffect, useCallback } from "react";
import { Application } from "@/contracts/auth-api.types";
import { applicationService } from "@/services/applicationService";
import { useNotification } from "@/hooks/useNotification";
import { Button } from "@/components/common/Button";
import { Badge } from "@/components/common/Badge";
import { PermissionGuard } from "@/components/guards/PermissionGuard";
import { Plus, Edit2, Trash2, Search, RefreshCw, ExternalLink, ShieldAlert } from "lucide-react";
import { formatDate, getErrorMessage } from "@/utils/formatters";
import { ApplicationModal } from "./ApplicationModal";

export const ApplicationsList: React.FC = () => {
  const { notify } = useNotification();
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [applicationToEdit, setApplicationToEdit] = useState<Application | null>(null);
  const [applicationToDelete, setApplicationToDelete] = useState<Application | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadApplications = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await applicationService.list(1, 100);
      const list = Array.isArray(response) ? response : response.data || [];
      setApplications(list);
    } catch (err: unknown) {
      notify(getErrorMessage(err, "Erro ao carregar lista de aplicações"), "error");
    } finally {
      setIsLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadApplications();
  }, [loadApplications]);

  const filteredApplications = applications.filter((app) => {
    const term = searchTerm.toLowerCase();
    return (
      app.name.toLowerCase().includes(term) ||
      app.client_id.toLowerCase().includes(term) ||
      (app.description && app.description.toLowerCase().includes(term))
    );
  });

  const handleDelete = async () => {
    if (!applicationToDelete) return;
    setIsDeleting(true);
    try {
      await applicationService.delete(applicationToDelete.id);
      notify(`Aplicação "${applicationToDelete.name}" removida com sucesso`, "success");
      setApplicationToDelete(null);
      await loadApplications();
    } catch (err: unknown) {
      notify(getErrorMessage(err, "Erro ao remover a aplicação"), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aplicações & Tenants</h1>
          <p className="text-muted text-sm mt-0.5">
            Gerencie os clientes OAuth2 e ecossistemas isolados no modo Multi-Tenant.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadApplications}
            isLoading={isLoading}
            title="Recarregar lista"
          >
            <RefreshCw size={16} />
            <span className="hide-sm">Recarregar</span>
          </Button>

          <PermissionGuard permission="applications:create">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setApplicationToEdit(null);
                setIsModalOpen(true);
              }}
            >
              <Plus size={16} />
              <span>Nova Aplicação</span>
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="search-input-wrapper flex-1">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome, client_id ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          {searchTerm && (
            <Button variant="ghost" size="sm" onClick={() => setSearchTerm("")}>
              Limpar
            </Button>
          )}
        </div>
      </div>

      {/* Applications Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading && (
          <div className="py-12 text-center">
            <div className="spinner mx-auto mb-3"></div>
            <p className="text-sm text-muted">Carregando aplicações cadastradas...</p>
          </div>
        )}

        {!isLoading && filteredApplications.length === 0 && (
          <div className="py-12 text-center text-muted">
            <p className="text-base font-medium">Nenhuma aplicação encontrada.</p>
            <p className="text-sm mt-1">Crie um novo tenant ou refine os termos da busca.</p>
          </div>
        )}

        {!isLoading && filteredApplications.length > 0 && (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Aplicação</th>
                  <th>Client ID</th>
                  <th>Status</th>
                  <th>Frontend URL</th>
                  <th>Access / Refresh TTL</th>
                  <th>Criado Em</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((app) => (
                  <tr key={app.id}>
                    <td>
                      <div>
                        <span className="font-semibold text-gray-900 block">{app.name}</span>
                        {app.description && (
                          <span className="text-xs text-muted truncate max-w-xs block">
                            {app.description}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <code className="bg-gray-100 text-primary font-mono text-xs px-2 py-1 rounded">
                        {app.client_id}
                      </code>
                    </td>
                    <td>
                      <Badge variant={app.is_active ? "success" : "danger"}>
                        {app.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </td>
                    <td>
                      <a
                        href={app.frontend_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <span className="truncate max-w-xs">{app.frontend_url}</span>
                        <ExternalLink size={12} />
                      </a>
                    </td>
                    <td>
                      <span className="text-xs font-mono text-gray-600">
                        {app.access_token_expires_in || "15m"} /{" "}
                        {app.refresh_token_expires_in || "7d"}
                      </span>
                    </td>
                    <td className="text-xs text-muted">{formatDate(app.created_at)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <PermissionGuard permission="applications:update">
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => {
                              setApplicationToEdit(app);
                              setIsModalOpen(true);
                            }}
                            title="Editar aplicação"
                            aria-label={`Editar aplicação ${app.name}`}
                          >
                            <Edit2 size={16} />
                          </button>
                        </PermissionGuard>

                        <PermissionGuard permission="applications:delete">
                          <button
                            type="button"
                            className="btn-icon text-red-500 hover:text-red-700"
                            onClick={() => setApplicationToDelete(app)}
                            title="Excluir aplicação"
                            aria-label={`Excluir aplicação ${app.name}`}
                            disabled={app.client_id === "auth-admin"}
                          >
                            <Trash2 size={16} />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <ApplicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={loadApplications}
        applicationToEdit={applicationToEdit}
      />

      {/* Delete Confirmation Modal */}
      {applicationToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <button
            type="button"
            className="modal-overlay"
            onClick={() => setApplicationToDelete(null)}
            aria-label="Cancelar exclusão"
            tabIndex={-1}
          />
          <div className="modal-container modal-sm">
            <div className="modal-header">
              <h3 className="modal-title flex items-center gap-2 text-red-600">
                <ShieldAlert size={20} />
                <span>Confirmar Exclusão</span>
              </h3>
            </div>
            <div className="modal-body space-y-3">
              <p className="text-sm text-gray-700">
                Tem certeza que deseja excluir o tenant <strong>{applicationToDelete.name}</strong>{" "}
                (<code>{applicationToDelete.client_id}</code>)?
              </p>
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded">
                <strong>Atenção:</strong> Esta ação removerá em cascata todos os usuários,
                permissões e sessões vinculadas a esta aplicação.
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setApplicationToDelete(null)}
                  disabled={isDeleting}
                >
                  Cancelar
                </Button>
                <Button variant="danger" size="sm" onClick={handleDelete} isLoading={isDeleting}>
                  Excluir Permanentemente
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
